<template>
  <v-card id="tract-rate-review">
    <v-card-title class="primary">
      <v-row justify="space-between" dense>
        <v-col cols="auto">
          <span class="headline white--text mr-2">
            {{ title }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideRefresh @close="$emit('close')"/>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate color="primary"/>
      <template v-else-if="aggregatedData.length === 0">
        <v-row class="mt-2 mb-n4">
          <v-col cols="12">
            <v-row align="center" justify="start" no-gutters>
              <v-col cols="auto">
                <v-select
                  v-model="selectedContractId"
                  :items="contractOptions"
                  :label="$t('contract')"
                  item-text="label"
                  item-value="contractId"
                  dense
                  outlined
                  hide-details
                  style="min-width: 220px; background: white; border-radius: 4px;"
                />
              </v-col>
              <v-col v-if="isSpecificContractSelected" cols="auto" class="d-flex align-center ml-2">
                <Icon
                  icon="mdi-altimeter"
                  icon-color="primary"
                  :tooltipText="$t('viewContract')"
                  @icon-clicked="openContractDetail"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <NoData class="mt-4">{{ emptyStateMessage }}</NoData>
      </template>
      <RateVerification
        v-else
        :items="filteredAggregatedData"
        :groupingKeys="groupingKeys"
        :expandToContract="true"
        :hideEditAndUnpostActions="true"
        :hideContractCards="isSpecificContractSelected"
        :tractId="tractId"
        :expansionPanelKey="expansionPanelKey"
        @contract-edited="onContractEdited"
      >
        <template #title>
          <v-row align="center" justify="start" no-gutters>
            <v-col cols="auto">
              <v-select
                v-model="selectedContractId"
                :items="contractOptions"
                :label="$t('contract')"
                item-text="label"
                item-value="contractId"
                dense
                outlined
                hide-details
                style="min-width: 220px; background: white; border-radius: 4px;"
              />
            </v-col>
            <v-col v-if="isSpecificContractSelected" cols="auto" class="d-flex align-center ml-2">
              <Icon
                icon="mdi-altimeter"
                icon-color="primary"
                :tooltipText="$t('viewContract')"
                @icon-clicked="openContractDetail"
              />
            </v-col>
          </v-row>
        </template>
      </RateVerification>
    </v-card-text>
    <Dialog :stateId="contractDialogId" @dialog-closing="closeContractDialog">
      <ContractDetail
        v-if="contractDetailOpen && !editContractOpen"
        :contractId="viewableContractId"
        @edit-contract="openEditContract"
        @edit-activities="openEditContract"
      />
      <EditContract
        v-if="editContractOpen"
        :contractId="viewableContractId"
        :contractMode="ContractMode.Logs"
        :companyRequiresEditApproval="companyInfo.requireApprovalForContractModifications"
        shouldAddActivity
        @contract-changed="onContractChanged"
        @contract-changed-keep-open="onContractEdited"
        @close="closeContractDialog"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { rateVerificationGroupingKeys } from '@/headers/Cycles.js'
import { aggregateRatesForTractContracts } from '@/components/settlements/rate-verification/rateVerificationAggregator.js'
import { ContractMode } from '@/utils/Enumerations.js'
import { uniqueDialogId } from '@/utils/componentHelpers.js'

const SCOPE_ALL_ID = -2
const SCOPE_FILTERED_ID = -3

export default {
  name: 'TractRateReview',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    RateVerification: () => import('@/components/settlements/rate-verification/RateVerification.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    EditContract: () => import('@/components/contract/ContractEditor.vue')
  },

  props: {
    tractId: {
      type: Number,
      required: true
    },
    contractIds: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data: () => ({
    loading: false,
    selectedContractId: SCOPE_FILTERED_ID,
    activeScope: 'filtered',
    cachedFilteredRates: null,
    cachedAllRates: null,
    contractDetailOpen: false,
    editContractOpen: false,
    contractDialogId: uniqueDialogId('tract-rate-review-contract'),
    ContractMode
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),

    activeRates () {
      if (this.activeScope === 'all') return this.cachedAllRates || []
      if (this.contractIds.length === 0) return []
      return this.cachedFilteredRates || []
    },

    title () {
      const tractName = this.activeRates[0]?.contractInfo?.tractName
      return tractName
        ? this.$t('rateReview') + ' - ' + tractName
        : this.$t('rateReview')
    },

    groupingKeys () {
      return rateVerificationGroupingKeys(true)
    },

    uniqueActiveContractIds () {
      return [...new Set(this.activeRates.map(s => s.contractId))]
    },

    isSpecificContractSelected () {
      if (this.selectedContractId !== SCOPE_ALL_ID && this.selectedContractId !== SCOPE_FILTERED_ID) return true
      return this.uniqueActiveContractIds.length === 1
    },

    viewableContractId () {
      if (this.selectedContractId !== SCOPE_ALL_ID && this.selectedContractId !== SCOPE_FILTERED_ID) return this.selectedContractId
      return this.uniqueActiveContractIds.length === 1 ? this.uniqueActiveContractIds[0] : null
    },

    aggregatedData () {
      return aggregateRatesForTractContracts(this.activeRates)
    },

    contractOptions () {
      const contracts = this.activeRates.map(s => ({
        contractId: s.contractId,
        label: [s.accountName, s.destinationName, s.settingName].filter(Boolean).join(' - ')
      }))
      const uniqueContracts = contracts.filter((c, i, arr) =>
        arr.findIndex(x => x.contractId === c.contractId) === i
      )
      return [
        { header: this.$t('scope') },
        { contractId: SCOPE_ALL_ID, label: this.$t('allTractContracts') },
        { contractId: SCOPE_FILTERED_ID, label: this.$t('filteredContractsN', { n: this.contractIds.length }) },
        { divider: true },
        { header: this.$t('contracts') },
        ...uniqueContracts
      ]
    },

    filteredAggregatedData () {
      if (this.selectedContractId === SCOPE_ALL_ID || this.selectedContractId === SCOPE_FILTERED_ID) return this.aggregatedData
      return this.aggregatedData
        .filter(entry => entry.contracts.some(c => c.contractId === this.selectedContractId))
        .map(entry => ({
          ...entry,
          contracts: entry.contracts.filter(c => c.contractId === this.selectedContractId)
        }))
    },

    expansionPanelKey () {
      return `${this.activeScope}-contract-${this.selectedContractId}`
    },

    emptyStateMessage () {
      if (this.activeScope === 'filtered' && this.contractIds.length === 0) {
        return this.$t('noContractsMatchFilter')
      }
      if (this.activeScope === 'filtered') {
        return this.$t('noRateDataForFiltered')
      }
      return this.$t('noRateDataForTract')
    }
  },

  watch: {
    selectedContractId (val) {
      if (val === SCOPE_ALL_ID) {
        this.activeScope = 'all'
        if (this.cachedAllRates === null) {
          this.fetchData('all')
        }
      } else if (val === SCOPE_FILTERED_ID) {
        this.activeScope = 'filtered'
      }
    }
  },

  created () {
    this.fetchData('filtered')
  },

  methods: {
    ...mapActions('tract', ['fetchRatesForTractContracts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    openContractDetail () {
      this.contractDetailOpen = true
      this.editContractOpen = false
      this.openOrUpdateDialog({ id: this.contractDialogId, width: '80%' })
    },

    openEditContract () {
      this.editContractOpen = true
    },

    closeContractDialog () {
      this.contractDetailOpen = false
      this.editContractOpen = false
      this.closeDialogsAtOrAbove(this.contractDialogId)
    },

    onContractChanged () {
      this.closeContractDialog()
      this.onContractEdited()
    },

    onContractEdited () {
      this.cachedFilteredRates = null
      this.cachedAllRates = null
      this.$emit('refresh-contracts')
      this.selectedContractId = SCOPE_FILTERED_ID
      this.fetchData(this.activeScope)
    },

    async fetchData (scope) {
      if (scope === 'filtered' && this.contractIds.length === 0) return
      this.loading = true
      try {
        const contractIds = scope === 'all' ? [] : this.contractIds
        const rates = await this.fetchRatesForTractContracts({ tractId: this.tractId, contractIds })
        if (scope === 'all') {
          this.cachedAllRates = rates
        } else {
          this.cachedFilteredRates = rates
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
